import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  LayoutDashboard, 
  Users, 
  Calendar, 
  Clock, 
  Shield, 
  TrendingUp, 
  LogOut, 
  Menu,
  X,
  Building2,
  Settings,
  UserCog,
  Gift,
  Target,
  ClipboardList,
  FileText
} from 'lucide-react';
import { useState } from 'react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ab04b277-779a-46f6-a15c-41b90e7d7254/artifacts/ccdpbqku_TBP-New%20Logo%20Design%20conv%201.png";

const navItems = [
  { href: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/admin/volunteers', label: 'Volunteers', icon: Users },
  { href: '/admin/tasks', label: 'Tasks', icon: ClipboardList },
  { href: '/admin/documents', label: 'Documents', icon: FileText },
  { href: '/admin/corporate', label: 'Corporate', icon: Building2 },
  { href: '/admin/corporate/outreach', label: 'Corporate Outreach', icon: Target },
  { href: '/admin/donations', label: 'Donations', icon: Gift },
  { href: '/admin/events', label: 'Events', icon: Calendar },
  { href: '/admin/hours', label: 'Hour Tracking', icon: Clock },
  { href: '/admin/background-checks', label: 'Background Checks', icon: Shield },
  { href: '/admin/impact', label: 'Impact Dashboard', icon: TrendingUp },
  { href: '/admin/admins', label: 'Admin Users', icon: UserCog },
  { href: '/admin/settings', label: 'Settings', icon: Settings },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/admin/login';
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-layout">
      {/* Mobile Header */}
      <header className="lg:hidden bg-white shadow-sm sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/admin/dashboard" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="The Bookshelf Project" className="h-8 w-auto" />
          </Link>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside 
          className={`
            fixed lg:sticky top-0 left-0 z-40
            w-64 h-screen bg-white shadow-lg
            transform transition-transform duration-300 ease-in-out
            lg:transform-none lg:translate-x-0
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="p-6 border-b hidden lg:block">
              <Link to="/admin/dashboard" className="flex items-center gap-3">
                <img src={LOGO_URL} alt="The Bookshelf Project" className="h-10 w-auto" />
              </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
              {navItems.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      sidebar-link flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium
                      ${isActive ? 'active' : 'text-slate-600 hover:text-[#1a3a5c]'}
                    `}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* User Section */}
            <div className="p-4 border-t">
              {user && (
                <div className="flex items-center gap-3 mb-4 px-2">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 bg-[#5cc8e8]/10 rounded-full flex items-center justify-center">
                      <span className="text-[#1a3a5c] font-medium">
                        {user.name?.[0] || user.email?.[0]}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1a3a5c] truncate">{user.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                  </div>
                </div>
              )}
              <Button 
                variant="outline" 
                className="w-full justify-start text-slate-600 hover:text-red-600 hover:border-red-200"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/20 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 min-h-screen">
          {children}
        </main>
      </div>
    </div>
  );
}
